import { isLocalMediaRef, isUploadFile } from "@/app/lib/common/image-uploader/image-descriptor";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";

export const mediaRefUrl = (
    mediaRef: MediaRef,
    options: {
        size?: 'thumbnail' | 'small' | 'medium' | 'large' | null
    } = {
        size: null
    }
): string => {
    if (typeof mediaRef === 'string') {
        return mediaRef;
    } else if (isUploadFile(mediaRef)) {
        if (options?.size && mediaRef.formats && mediaRef.formats[options.size]) {
            return mediaRef.formats[options.size].url;
        }

        return mediaRef.url;
    } else if (isLocalMediaRef(mediaRef)) {
        return URL.createObjectURL(mediaRef.file as Blob);
    } else {
        console.error("provided 'src' prop is none of the expected types. Expected 'string', 'UploadFile' or 'LocalMediaRef'.", mediaRef);
        return PLACEHOLDER_IMAGE;
    }
}

export const mediaRefAlt = (mediaRef: MediaRef): string | undefined => {
    if (typeof mediaRef === 'string') {
        return undefined;
    } else if (isUploadFile(mediaRef)) {
        return mediaRef.alternativeText ?? undefined;
    } else if (isLocalMediaRef(mediaRef)) {
        return mediaRef.name ?? undefined;
    } else {
        console.error("provided 'src' prop is none of the expected types. Expected 'string', 'UploadFile' or 'LocalMediaRef'.", mediaRef);
        return '';
    }
}

const VIDEO_EXT = ['.mp4', '.webm', '.ogg', '.ogv', '.mov', '.m4v'];

export const mediaRefType = (mediaRef: MediaRef): 'image' | 'video' | 'pdf' => {
    // Detect by mime when UploadFile is provided
    if (isUploadFile(mediaRef) && mediaRef.mime === 'application/pdf') {
        return 'pdf';
    }

    if (isUploadFile(mediaRef) && mediaRef.mime && mediaRef.mime.startsWith('video/')) {
        return 'video';
    }

    if (isLocalMediaRef(mediaRef)) {
        const fileName = mediaRef.file.name.toLowerCase();
        if (fileName.endsWith('.pdf')) return 'pdf';
        if (VIDEO_EXT.some(ext => fileName.endsWith(ext))) return 'video';
    }

    // For plain string URLs, use file extension heuristics
    if (typeof mediaRef === 'string') {
        const lower = mediaRef.split('?')[0].split('#')[0].toLowerCase();
        if (lower.endsWith('.pdf')) return 'pdf';
        if (VIDEO_EXT.some(ext => mediaRef.endsWith(ext))) return 'video';
    }

    return 'image';
}

export const mediaRefName = (mediaRef: MediaRef | undefined): string | undefined => {
    if (!mediaRef) {
        return undefined;
    } else if (typeof mediaRef === 'string') {
        return mediaRef.split('/').pop() || undefined;
    } else if (isUploadFile(mediaRef)) {
        return mediaRef.name?.replace(mediaRef.ext || '', '');
    } else if (isLocalMediaRef(mediaRef)) {
        return mediaRef.name?.replace(/\.[^/.]+$/, "");
    } else {
        return undefined;
    }
}

export const mediaRefCaption = (mediaRef: MediaRef | undefined): string | undefined => {
    if (!mediaRef) {
        return undefined;
    } else if (typeof mediaRef === 'string') {
        return undefined;
    } else if (isUploadFile(mediaRef)) {
        return mediaRef.caption || undefined;
    } else if (isLocalMediaRef(mediaRef)) {
        return mediaRef.caption || undefined;
    } else {
        return undefined;
    }
}

export const mediaRefDimensions = (mediaRef: MediaRef | undefined): { width?: number, height?: number } | undefined => {
    if (!mediaRef) {
        return undefined;
    } else if (typeof mediaRef === 'string') {
        return undefined;
    } else if (isUploadFile(mediaRef)) {
        return {
            width: mediaRef.width ?? undefined,
            height: mediaRef.height ?? undefined
        }
    } else {
        return undefined;
    }
}